/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';

import { getCookieValue, setCookieValue } from '@/Client/Lib/cookieLib';
import {
  SETTINGS_WIDGET_BANNER_BODY_STYLE,
  SETTINGS_WIDGET_BANNER_TITLE_STYLE,
  SETTINGS_WIDGET_COLORS,
  SETTINGS_WIDGET_SPACING,
} from './SettingsStyleLib';

/** One thing the banner has to say, dismissible in its own right. */
export type SettingsInsight = {
  title: string;
  body: string;
  dismissCookieKey: string;
};

type SettingsInsightBannerProps = {
  title?: string;
  body?: string;
  confirmLabel?: string;
  dismissCookieKey?: string;
  /**
   * Several insights for one banner, shown in turn. A page keeps one banner at its top however
   * many sections it carries, rather than pinning one to each (KAIDEV-4345).
   */
  insights?: readonly SettingsInsight[];
};

/** Long enough to read a sentence twice before it moves on. */
const ROTATION_INTERVAL_MS = 8000;

const BANNER_STYLE = css`
  label: BANNER_STYLE;
  min-height: 60px;
  border-bottom: 1px solid ${SETTINGS_WIDGET_COLORS.border};
  background: ${SETTINGS_WIDGET_COLORS.infoBlueBackground};
  padding: 8px ${SETTINGS_WIDGET_SPACING.gutterX}px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ICON_STYLE = css`
  label: ICON_STYLE;
  width: 24px;
  height: 24px;
  color: ${SETTINGS_WIDGET_COLORS.infoBlue};
  flex: 0 0 auto;
`;

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MESSAGE_STYLE = css`
  label: MESSAGE_STYLE;
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
`;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  ${SETTINGS_WIDGET_BANNER_TITLE_STYLE}
`;

const BODY_STYLE = css`
  label: BODY_STYLE;
  ${SETTINGS_WIDGET_BANNER_BODY_STYLE}
`;

const DOTS_STYLE = css`
  label: DOTS_STYLE;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

const DOT_STYLE = css`
  label: DOT_STYLE;
  width: 7px;
  height: 7px;
  padding: 0;
  border: 1px solid ${SETTINGS_WIDGET_COLORS.infoBlue};
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
`;

const DOT_CURRENT_STYLE = css`
  label: DOT_CURRENT_STYLE;
  background: ${SETTINGS_WIDGET_COLORS.infoBlue};
`;

const CONFIRM_LINK_STYLE = css`
  label: CONFIRM_LINK_STYLE;
  ${SETTINGS_WIDGET_BANNER_TITLE_STYLE}
  text-decoration: underline;
  text-decoration-skip-ink: none;
  white-space: nowrap;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: ${SETTINGS_WIDGET_COLORS.infoBlue};
  }
`;

const InsightsIcon = () => (
  <svg css={ICON_STYLE} viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 3V21'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M3 12H21'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M7.5 7.5L16.5 16.5'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16.5 7.5L7.5 16.5'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const SettingsInsightBanner = ({
  title = 'Project Settings Insight',
  body =
    'Define your project types by giving each one a short label and description. The project type will appear as the first tag label on every project throughout Kai.',
  confirmLabel = 'Got it!',
  dismissCookieKey,
  insights,
}: SettingsInsightBannerProps) => {
  /* One insight or several, handled the same way below: a single banner is the one-element case
     of a rotating one. */
  const allInsights = useMemo<readonly SettingsInsight[]>(
    () => insights?.length ? insights : [{ title, body, dismissCookieKey: dismissCookieKey ?? '' }],
    [body, dismissCookieKey, insights, title],
  );

  const [dismissedKeys, setDismissedKeys] = useState<readonly string[]>(
    () => allInsights.map(insight => insight.dismissCookieKey).filter(key => key && getCookieValue(key) === '1'),
  );
  const [index, setIndex] = useState(0);
  /* Rotation stops while someone is reading or tabbing through: moving the words out from under
     a reader is the one thing a rotating banner must not do.

     Hover and focus are tracked apart because either one alone is reason enough to hold. Sharing
     one flag meant whichever ended last spoke for both: moving the mouse away resumed rotation
     with a dot still focused, and tabbing between the dots fired a blur that resumed it under a
     stationary pointer (KAIDEV-4345 review). */
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const isPaused = isHovered || hasFocusWithin;

  const visibleInsights = useMemo(
    () => allInsights.filter(insight => !dismissedKeys.includes(insight.dismissCookieKey)),
    [allInsights, dismissedKeys],
  );

  const currentIndex = visibleInsights.length ? index % visibleInsights.length : 0;

  useEffect(() => {
    if (isPaused || visibleInsights.length < 2) return;
    const timer = setInterval(() => setIndex(current => current + 1), ROTATION_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isPaused, visibleInsights.length]);

  const current = visibleInsights[currentIndex];
  if (!current) return null;

  const handleDismiss = () => {
    // Without a cookie key there is nothing to remember, so the banner is not dismissible.
    if (!current.dismissCookieKey) return;
    setCookieValue(current.dismissCookieKey, '1');
    setDismissedKeys(keys => [...keys, current.dismissCookieKey]);
    setIndex(0);
  };

  return (
    <div
      css={BANNER_STYLE}
      onBlur={event => {
        // Focus moving between the banner's own controls is not focus leaving the banner.
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        setHasFocusWithin(false);
      }}
      onFocus={() => setHasFocusWithin(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <InsightsIcon />
      <div css={CONTENT_STYLE}>
        <div css={MESSAGE_STYLE}>
          <div css={TITLE_STYLE}>{current.title}</div>
          <div css={BODY_STYLE}>{current.body}</div>
        </div>

        {
          /* Rotation on its own is not something a reader can steer, so it comes with the means
             to step through it - and to get back to one they only half read. */
        }
        {visibleInsights.length > 1
          ? (
            <div css={DOTS_STYLE}>
              {visibleInsights.map((insight, insightIndex) => (
                <button
                  key={insight.dismissCookieKey || insight.title}
                  aria-current={insightIndex === currentIndex}
                  aria-label={`Show ${insight.title}`}
                  css={[DOT_STYLE, insightIndex === currentIndex && DOT_CURRENT_STYLE]}
                  onClick={() => setIndex(insightIndex)}
                  type='button'
                />
              ))}
            </div>
          )
          : null}

        <button type='button' css={CONFIRM_LINK_STYLE} onClick={handleDismiss}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
};
